<template>
  <OnboardingLayout
    :is-next-allowed="true"
    :is-end="false"
    :is-next-loading="isNextLoading"
    :is-prev-button-shown="true"
    @next-step="$emit('nextStep')"
    @prev-step="$emit('prevStep')"
  >
    <template #left>
      <h1>Invite your colleagues.</h1>
      <h2>Routine is a lot more powerful when used with other people.</h2>

      <section>
        <div class="email">
          <FormInput
            ref="inputRef"
            v-model:value="email"
            type="email"
            placeholder="Email…"
            :state="errorInEmail ? 'error' : 'default'"
            @keydown="onInputKeydown"
          />
          <BaseButton
            class="invite"
            kind="main"
            type="primary"
            text="Invite"
            @click="inviteOne"
          />
        </div>

        <p v-if="sentMessage" class="success">
          {{ sentMessage }}
        </p>
        <p v-if="errorInEmail" class="error">
          Email is incorrect, please fix it and try again.
        </p>
      </section>
    </template>

    <template #right>
      <img :src="getImageUrl(imgName)" />
    </template>
  </OnboardingLayout>
</template>

<script lang="ts">
import { defineComponent, nextTick, onBeforeUnmount, ref } from "vue";

import BaseButton from "@/components/base/BaseButton.vue";
import FormInput from "@/components/graph/definition/form/FormInput.vue";
import * as inject from "@/components/inject";
import OnboardingLayout from "@/components/onboarding/OnboardingLayout.vue";
import { metrics } from "@/metrics";

export default defineComponent({
  components: {
    BaseButton,
    FormInput,
    OnboardingLayout,
  },
  props: {
    isNextLoading: { type: Boolean, required: false, default: false },
  },
  emits: {
    nextStep: () => true,
    prevStep: () => true,
  },
  setup() {
    const controller = inject.controller();
    const imgName = ref<string>("step_org");
    const email = ref("");
    const inputRef = ref<InstanceType<typeof FormInput> | null>(null);
    const errorInEmail = ref(false);
    const sentMessage = ref<string | null>(null);
    const sentMessageTimeoutId = ref<number | null>(null);
    const emailRegexp =
      /^([^<>()[\]\\,;:\s@"]+)@(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,})$/;

    const emailValid = (email: string) => !!email.match(emailRegexp);

    const focusInput = async () => {
      await nextTick();
      window.requestAnimationFrame(() => {
        inputRef.value?.focus?.();
      });
    };

    const showSentMessage = (invitedEmail: string) => {
      sentMessage.value = `Invite sent to ${invitedEmail}`;
      if (sentMessageTimeoutId.value != null) {
        window.clearTimeout(sentMessageTimeoutId.value);
      }
      sentMessageTimeoutId.value = window.setTimeout(() => {
        sentMessage.value = null;
        sentMessageTimeoutId.value = null;
      }, 2500);
    };

    onBeforeUnmount(() => {
      if (sentMessageTimeoutId.value != null) {
        window.clearTimeout(sentMessageTimeoutId.value);
      }
    });

    const inviteOne = async () => {
      const toInvite = email.value.trim();
      if (!toInvite || !emailValid(toInvite)) {
        errorInEmail.value = true;
        return;
      }
      errorInEmail.value = false;

      controller.inviteByEmail(toInvite);
      showSentMessage(toInvite);
      email.value = "";
      await focusInput();
    };

    const onInputKeydown = async (event: KeyboardEvent) => {
      if (event.key !== "Enter") return;
      event.preventDefault();
      event.stopPropagation();
      await inviteOne();
    };

    metrics.segment.page("Onboarding", { step: "invite" });

    const getImageUrl = (name: string) => {
      return new URL(`../../assets/onboarding/${name}.png`, import.meta.url)
        .href;
    };

    return {
      email,
      inputRef,
      errorInEmail,
      sentMessage,
      emailRegexp,
      inviteOne,
      onInputKeydown,
      imgName,
      getImageUrl,
    };
  },
});
</script>

<style scoped lang="scss">
p {
  @extend %font-body-regular;

  &.success {
    @extend %font-caption-regular;
    margin-top: 8px;
    @include themed() {
      color: t($primary);
    }
  }

  &.error {
    @include themed() {
      color: t($secondary);
    }
  }
}

.email {
  width: 100%;
  margin-bottom: 12px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  column-gap: 12px;

  .base-button {
    flex-shrink: 0;
  }
}
</style>
